import { Dayjs } from "dayjs";
import { Booking, BookingStatus } from "src/business/booking";
import { DayHoursList } from "src/business/restaurant/restaurant";
import {
  SLOT_INTERVAL_MINUTES,
  openWindowsForDate,
  rowForSlotLabel,
  rowSpanForDuration,
} from "src/components/Main/Pages/TableSchedule/dayGridHelpers";

/** Below this share of the floor, an hour reads as "quiet". */
const QUIET_RATIO = 0.25;

/** Rows are 30-minute increments on the table-schedule grid. */
const ROWS_PER_HOUR = 60 / SLOT_INTERVAL_MINUTES;

export interface ServiceHour {
  /** Hour of the day, 0–23. */
  hour: number;
  /** Tables held for any part of that hour. */
  tables: number;
  /** Share of the bar to fill, 0–1. */
  fill: number;
  quiet: boolean;
}

/** The longest run of consecutive quiet hours in a service. */
export interface QuietWindow {
  startHour: number;
  /** Exclusive — a 15:00→17:00 window ends at 17. */
  endHour: number;
}

/** A cancelled booking has freed its table, so it does not load the service. */
const isExpected = (booking: Booking): boolean =>
  booking.bookingStatus !== BookingStatus.CANCELED &&
  booking.bookingStatus !== BookingStatus.NO_SHOW;

/** The stretch of grid rows one booking holds its table for; `end` exclusive. */
interface HeldRows {
  start: number;
  end: number;
}

/**
 * Rows each expected booking holds, on the day's 48-row grid. A booking holds
 * one table for the restaurant's booking duration, whether or not it has been
 * seated on the board — an unassigned booking still needs a table.
 */
const heldRowsForBookings = (
  bookings: Booking[],
  bookingDuration: number,
): HeldRows[] => {
  const span = rowSpanForDuration(bookingDuration);
  return bookings.filter(isExpected).map((booking) => {
    const start = rowForSlotLabel(booking.bookingHour);
    return { start, end: start + span };
  });
};

/** Hours the restaurant is open on `date`, in ascending order. */
const openHoursForDate = (
  schedule: DayHoursList,
  date: Dayjs,
  leaves: string[],
): number[] => {
  const hours = new Set<number>();
  openWindowsForDate(schedule, date, leaves).forEach(({ startRow, endRow }) => {
    for (let row = startRow; row < endRow; row += 1) {
      hours.add(Math.floor(row / ROWS_PER_HOUR));
    }
  });
  return [...hours].sort((left, right) => left - right);
};

/**
 * Tables held per open hour of `date`'s service.
 *
 * The floor is what fills up, so an hour is measured in tables rather than in
 * guests: two couples at separate tables take as much of the service as two
 * parties of six. A booking counts towards every hour it overlaps, since it
 * holds its table for the whole booking duration — a 12:30 booking running 90
 * minutes occupies both 12h and 13h.
 *
 * Bars are filled against the restaurant's table count when it is known, so a
 * fully booked hour reads as full. Without a table count on record the only
 * meaningful reference left is the day's own busiest hour, so bars are scaled
 * against that instead — the shape of the service still shows, which is all the
 * quiet-window callout needs.
 */
export const buildServiceOccupancy = ({
  bookings,
  schedule,
  leaves,
  date,
  numberOfTables,
  bookingDuration,
}: {
  bookings: Booking[];
  schedule: DayHoursList;
  leaves: string[];
  date: Dayjs;
  numberOfTables: number | null;
  bookingDuration: number;
}): ServiceHour[] => {
  const openHours = openHoursForDate(schedule, date, leaves);
  if (openHours.length === 0) return [];

  const held = heldRowsForBookings(bookings, bookingDuration);

  const hours = openHours.map((hour) => {
    const hourStart = hour * ROWS_PER_HOUR;
    const hourEnd = hourStart + ROWS_PER_HOUR;
    return {
      hour,
      tables: held.filter(
        (rows) => rows.start < hourEnd && hourStart < rows.end,
      ).length,
    };
  });

  const peak = hours.reduce((max, { tables }) => Math.max(max, tables), 0);
  const reference = numberOfTables && numberOfTables > 0 ? numberOfTables : peak;

  return hours.map(({ hour, tables }) => ({
    hour,
    tables,
    fill: reference > 0 ? Math.min(tables / reference, 1) : 0,
    quiet: reference === 0 || tables <= reference * QUIET_RATIO,
  }));
};

/**
 * The longest stretch of consecutive quiet hours — the window worth filling
 * with an offer. `null` only when no hour is quiet.
 *
 * A service that is quiet end to end still yields its longest open stretch:
 * "the whole evening is empty" is a window worth pitching an offer into, not a
 * reason to say nothing. Telling that apart from a service with no bookings at
 * all is the caller's job, since the two want different words.
 */
export const findQuietWindow = (hours: ServiceHour[]): QuietWindow | null => {
  if (hours.length === 0) return null;

  let best: QuietWindow | null = null;
  let runStart: number | null = null;
  let runEnd = 0;

  for (let index = 0; index <= hours.length; index += 1) {
    const entry = index < hours.length ? hours[index] : null;
    // A run continues only across adjacent hours: a service with a midday break
    // must not merge its two sides into one window.
    const extends_ =
      entry !== null && entry.quiet && runStart !== null && runEnd === entry.hour;

    if (extends_ && entry) {
      runEnd = entry.hour + 1;
      continue;
    }

    if (runStart !== null) {
      const bestLength = best ? best.endHour - best.startHour : 0;
      if (runEnd - runStart > bestLength) {
        best = { startHour: runStart, endHour: runEnd };
      }
      runStart = null;
    }

    if (entry !== null && entry.quiet) {
      runStart = entry.hour;
      runEnd = entry.hour + 1;
    }
  }

  return best;
};

/** Thirty-minute slots the restaurant is open for on `date`. */
const openSlotsForDate = (
  schedule: DayHoursList,
  date: Dayjs,
  leaves: string[],
): number =>
  openWindowsForDate(schedule, date, leaves).reduce(
    (total, { startRow, endRow }) => total + (endRow - startRow),
    0,
  );

/**
 * Share of the day's table-slots that bookings hold, 0–100.
 *
 * What a service can sell is its floor plan over time: every table, on every
 * slot the restaurant is open. A booking holds one table for its whole
 * duration, so it takes as many table-slots as that duration covers — which is
 * what makes a packed lunch-only restaurant read as busy rather than as a
 * fraction of a day it never opens for.
 *
 * `null` when there is nothing to measure against: no tables on record, or no
 * opening hours today.
 */
export const computeOccupancyRate = ({
  bookings,
  schedule,
  leaves,
  date,
  numberOfTables,
  bookingDuration,
}: {
  bookings: Booking[];
  schedule: DayHoursList;
  leaves: string[];
  date: Dayjs;
  numberOfTables: number | null;
  bookingDuration: number;
}): number | null => {
  if (!numberOfTables || numberOfTables <= 0) return null;

  const openSlots = openSlotsForDate(schedule, date, leaves);
  if (openSlots === 0) return null;

  const filledSlots = heldRowsForBookings(bookings, bookingDuration).reduce(
    (total, rows) => total + (rows.end - rows.start),
    0,
  );

  // Bookings may sit outside the opening hours the rate is measured against, so
  // the ratio is capped rather than allowed to read over 100%.
  return Math.min(
    Math.round((filledSlots / (numberOfTables * openSlots)) * 100),
    100,
  );
};

/** Total guests expected across the service. */
export const totalExpectedSeats = (bookings: Booking[]): number =>
  bookings
    .filter(isExpected)
    .reduce((total, booking) => total + booking.numberOfSeats, 0);
